import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Label } from '../../../components/ui/Label';

const DELIVERY_METHODS = [
  { value: 'email', label: 'Email only' },
  { value: 'postcard', label: 'Postcard only' },
  { value: 'both', label: 'Email + postcard' },
];

function DeliveryPreferencesForm({ preferences, onSave, onDisable, isUpdating }) {
  const [enabled, setEnabled] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('email');
  const [mailingName, setMailingName] = useState('');
  const [mailingAddress, setMailingAddress] = useState('');

  useEffect(() => {
    if (preferences) {
      setEnabled(preferences.mailSubscriptionEnabled ?? false);
      setDeliveryMethod(preferences.mailDeliveryMethod ?? 'email');
      setMailingName(preferences.mailingName ?? '');
      setMailingAddress(preferences.mailingAddress ?? '');
    }
  }, [preferences]);

  const needsAddress = deliveryMethod === 'postcard' || deliveryMethod === 'both';

  const handleSave = (e) => {
    e.preventDefault();
    onSave({
      mailSubscriptionEnabled: enabled,
      mailDeliveryMethod: deliveryMethod,
      mailingName: needsAddress ? mailingName : undefined,
      mailingAddress: needsAddress ? mailingAddress : undefined,
    });
  };

  return (
    <div className="border-2 border-neutral-200 rounded-lg p-6 mb-6">
      <h2 className="text-lg font-semibold text-neutral-900 mb-4">Delivery Preferences</h2>

      <form onSubmit={handleSave} className="space-y-5">
        {/* Enable toggle */}
        <div className="flex items-center gap-3">
          <input
            id="mailEnabled"
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="w-4 h-4 accent-neutral-900"
          />
          <Label htmlFor="mailEnabled" className="cursor-pointer">
            Enable automatic ballot guide delivery
          </Label>
        </div>

        {enabled && (
          <>
            {/* Delivery method */}
            <div>
              <Label htmlFor="deliveryMethod">Delivery method</Label>
              <select
                id="deliveryMethod"
                value={deliveryMethod}
                onChange={(e) => setDeliveryMethod(e.target.value)}
                className="mt-1 flex h-10 w-full border-2 border-neutral-900 bg-[#fefcf6] px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:border-red-700"
              >
                {DELIVERY_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Address fields for postcard/both */}
            {needsAddress && (
              <>
                <div>
                  <Label htmlFor="mailingName">Your name</Label>
                  <Input
                    id="mailingName"
                    placeholder="Full name"
                    value={mailingName}
                    onChange={(e) => setMailingName(e.target.value)}
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="mailingAddress">Mailing address</Label>
                  <Input
                    id="mailingAddress"
                    placeholder="Street, City, State ZIP"
                    value={mailingAddress}
                    onChange={(e) => setMailingAddress(e.target.value)}
                    className="mt-1"
                    required
                  />
                </div>
              </>
            )}
          </>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={isUpdating}>
            {isUpdating ? 'Saving...' : 'Save preferences'}
          </Button>
          {preferences?.mailSubscriptionEnabled && (
            <Button type="button" variant="outline" onClick={onDisable} disabled={isUpdating}>
              Disable delivery
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

DeliveryPreferencesForm.propTypes = {
  preferences: PropTypes.shape({
    mailSubscriptionEnabled: PropTypes.bool,
    mailDeliveryMethod: PropTypes.string,
    mailingName: PropTypes.string,
    mailingAddress: PropTypes.string,
  }),
  onSave: PropTypes.func.isRequired,
  onDisable: PropTypes.func.isRequired,
  isUpdating: PropTypes.bool,
};

DeliveryPreferencesForm.defaultProps = {
  preferences: null,
  isUpdating: false,
};

export default DeliveryPreferencesForm;
